import { QueryClient } from '@tanstack/react-query';
import {
  PersistedClient,
  Persister
} from '@tanstack/react-query-persist-client';
import { del, get, set } from 'idb-keyval';

let BACKEND_URL = 'http://localhost:3001';

if (process.env.NEXT_PUBLIC_BACKEND_URL) {
  BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
}

let BACKEND_ORGANIZATION_API_KEY = 'c92a6349e3fc42eaadecc85e5e671841';

if (process.env.NEXT_PUBLIC_BACKEND_API_KEY) {
  BACKEND_ORGANIZATION_API_KEY = process.env.NEXT_PUBLIC_BACKEND_API_KEY;
}

function makeQueryClient () {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        // Data can be fresh or stale, and staleTime defines when it goes stale.
        // When the data is marked as stale, once the user comes back to the page, the data will be refetched.
        staleTime: 1000 * 60 * 60 * 24,
        retry: 1,
        // Keep unused data for infinity amount of time (Garbage collect Time)
        // We keep the data forever to get the email address of the user in the sidebar as we don't have a request in the backend to get it
        gcTime: Infinity,
        // Disable automatic interval fetching
        refetchInterval: 0,
        refetchIntervalInBackground: false,
        refetchOnMount: true
      }
    }
  });
}

let browserQueryClient: QueryClient | undefined;

function getQueryClient () {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important so we don't re-make a new client if React
    // supsends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

// In RTK Query we can find tagTypes in createAPI function.
// In order to have a single place to check the tagTypes we will use this object

const TAG_TYPES = {
  AUTH: 'AUTH',
  PROJECTS: 'PROJECTS',
  CLOCKINGS: 'CLOCKINGS',
  USERS: 'USERS',
  USER: 'USER',
  APP_USERS: 'APP_USERS',
  BACKOFFICE_USERS: 'BACKOFFICE_USERS',
  SYSTEM_LOGS: 'SYSTEM_LOGS',
  MATCHES: 'MATCHES',
  MATCHMAKERS: 'MATCHMAKERS',
  AI_CONFIGS: 'AI_CONFIGS',
  MODERATION_REJECTION_HISTORY: 'MODERATION_REJECTION_HISTORY',
  STATISTICS: 'STATISTICS',
  PUBLISHING_ARTICLES: 'PUBLISHING_ARTICLES',
  PUBLISHING_MATCHMAKERS: 'PUBLISHING_MATCHMAKERS',
  PUBLISHING_MATCHMAKER_SETTINGS: 'PUBLISHING_MATCHMAKER_SETTINGS',
  TESTING_CHATS: 'TESTING_CHATS'
} as const;

function createIDBPersister (idbValidKey: IDBValidKey) {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, client);
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey);
    },
    removeClient: async () => {
      await del(idbValidKey);
    }
  } as Persister;
}

const persister = createIDBPersister('queryClient.v1');

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
export {
  BACKEND_URL,
  BACKEND_ORGANIZATION_API_KEY,
  getQueryClient,
  persister,
  TAG_TYPES,
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY
};
